import type { AuditEvent } from '../../api/types';
import { asObject } from './auditSession';

/**
 * How far into the session a row happened: `00:04`, `02:12`, `1:03:40`.
 *
 * Not the wall clock and not "how long ago". Inside one session what a reader
 * is placing is the order and the spacing — the command four seconds in, the
 * refusal at half a minute, the close two minutes later — and an absolute time
 * repeated down the rail buries that under six identical prefixes.
 */
export function offsetFrom(startAt: number, at: number): string {
  const total = Math.max(0, at - startAt);
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours === 0 ? `${pad(minutes)}:${pad(seconds)}` : `${String(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** As much of a value as a rail holds. The whole of it is in the panel above. */
const RAIL = 90;

/**
 * The one thing a detail carries that is worth a line of its own.
 *
 * Most of a detail is context; a few keys carry what actually happened — why
 * something was refused, the statement that was issued, the path a call took —
 * and the rail shows that rather than the whole string.
 *
 * **Both forms, told apart by the row.** In the object a current cluster seals,
 * these are fields and are read as fields, under the names gdsgate's own
 * dictionary gives them: `reason`, `query`, `path`. In the `key=value` text of
 * an older row they are found by pattern, in the spellings those rows used —
 * including `cmd` and `statement`, which the object form has no keys for and
 * which no amount of reading the current dictionary would ever explain. That
 * branch is not a transition: those rows are sealed, and they outlive every
 * dictionary this console will be taught.
 */
export function saidBy(detail: string): string | undefined {
  const structured = asObject(detail);
  if (structured !== undefined) {
    for (const key of ['reason', 'query', 'path']) {
      const value = structured[key];
      if (typeof value === 'string' && value !== '') return rail(value);
    }
    return undefined;
  }
  for (const key of ['reason', 'cmd', 'statement', 'path']) {
    const match = new RegExp(`${key}=([^;]+)`).exec(detail);
    if (match?.[1] !== undefined) return match[1].trim();
  }
  return undefined;
}

/**
 * One line's worth of a value that has no length limit.
 *
 * A statement can be a page long and a path carries its whole query string, so
 * both are flattened and cut. Cut visibly, with the ellipsis, because a rail
 * showing the first ninety characters of a query as if that were the query is
 * the one thing worse than showing none of it.
 */
function rail(value: string): string {
  const flat = value.replace(/\s+/g, ' ').trim();
  return flat.length > RAIL ? `${flat.slice(0, RAIL - 1)}…` : flat;
}

/** Whether this row is where the session was refused something. */
export function isRefusal(event: AuditEvent): boolean {
  const word = event.outcome.toLowerCase();
  return word.includes('deny') || word.includes('fail') || word.includes('refus');
}
